import { Call, ApiUrl, ParseGeneralResponse } from "./ApiUtils";

export function fetchOrderUnits(orderId) {
  const url = ApiUrl(`v2/order_item_units?order_id=${orderId}`);
  return Call(
    "get",
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        summary: "summary",
        items: "items",
        customer_order_number: "customer_order_number",
        warehouse_code: "warehouse_code",
        account_dim_factor: "account_dim_factor",
        required_dims: "required_dims",
        dim_weight_basis: "dim_weight_basis",
      },
    }
  );
}

export function updateUnit(unitId, unitAttrs) {
  const url = ApiUrl(`v2/order_item_units/${unitId}`);
  return Call(
    "patch",
    url,
    { unit: unitAttrs },
    ParseGeneralResponse,
    {
      responseDataKeys: { unit: "unit", summary: "summary" },
    }
  );
}

export function bulkUpdateUnits(unitIds, unitAttrs) {
  const url = ApiUrl("v2/order_item_units/bulk_update");
  return Call(
    "patch",
    url,
    { unit_ids: unitIds, unit: unitAttrs },
    ParseGeneralResponse,
    {
      responseDataKeys: { units: "units", summary: "summary" },
    }
  );
}

// Heterogeneous per-unit batch (different attrs per unit) for the Items &
// Measure Save flush — see OrderItemUnits#bulk_upsert. `updates` is
// [{ unit_id, ...attrs }]; the affected items rebalance once and the order
// rolls up once, all in a single request.
export function bulkUpsertUnits(updates) {
  const url = ApiUrl("v2/order_item_units/bulk_upsert");
  return Call("patch", url, { updates }, ParseGeneralResponse, {
    // Full index-style payload so the Save flush refreshes the screen from this
    // response (no reload GET).
    responseDataKeys: {
      items: "items",
      account_dim_factor: "account_dim_factor",
      required_dims: "required_dims",
      dim_weight_basis: "dim_weight_basis",
      summary: "summary",
    },
  });
}

// B1: server-side item duplicate — see
// API::V2::OrderItemUnitsController#duplicate_item. Atomically duplicates
// the item AND overlays the source units' manual piece splits/booked+reweigh
// measurements/storage locations onto the new item's freshly-minted units,
// so nothing is lost the way the old create-based duplicate silently
// dropped it.
export function duplicateItem(orderItemId) {
  const url = ApiUrl("v2/order_item_units/duplicate_item");
  return Call(
    "post",
    url,
    { order_item_id: orderItemId },
    ParseGeneralResponse,
    {
      responseDataKeys: { item: "item" },
    }
  );
}

export function swapUnitLabels(unitIdA, unitIdB) {
  const url = ApiUrl("v2/order_item_units/swap_labels");
  return Call(
    "post",
    url,
    { unit_id_a: unitIdA, unit_id_b: unitIdB },
    ParseGeneralResponse,
    {
      responseDataKeys: { units: "units", summary: "summary" },
    }
  );
}

export function fetchUnitLabels(unitIds) {
  const unitIdParams = unitIds.map(id => `unit_ids[]=${id}`).join("&");
  const url = ApiUrl(`v2/order_item_units/labels?${unitIdParams}`);
  return Call(
    "get",
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: { labels_pdf: "labels_pdf", file_name: "file_name" },
    }
  );
}

// Items & Measure "Print Labels" from the order listing: generate scannable-unit
// labels for every unit across the selected orders (server skips orders with no
// units and errors only when none have any). Org-scoped since order ids are
// client-sent.
export function fetchUnitLabelsForOrders(orderIds, organizationId) {
  const orderIdParams = orderIds.map(id => `order_ids[]=${id}`).join("&");
  const orgParam = organizationId ? `&organization_id=${organizationId}` : "";
  const url = ApiUrl(`v2/order_item_units/labels?${orderIdParams}${orgParam}`);
  return Call(
    "get",
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: { labels_pdf: "labels_pdf", file_name: "file_name" },
    }
  );
}

export function setDeclaredValue(orderId, declaredValue) {
  const url = ApiUrl("v2/order_item_units/set_declared_value");
  return Call(
    "patch",
    url,
    { order_id: orderId, declared_value: declaredValue },
    ParseGeneralResponse,
    {
      responseDataKeys: { declared_value: "declared_value", summary: "summary" },
    }
  );
}

export function fetchUnitHistory(unitId) {
  const url = ApiUrl(`v2/order_item_units/${unitId}/history`);
  return Call(
    "get",
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: { status_logs: "status_logs", measurements: "measurements" },
    }
  );
}
